import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import { Mail, Phone, MapPin, CheckCircle, AlertCircle, Home as HomeIcon } from "lucide-react";

export default function ShippingPolicy() {
  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <Header />
      <PageBanner
        title="Shipping Policy"
        description="Digital Services - No Physical Shipping Involved"
        backgroundImage="/assets/banner-bg.jpg"
      />
      
      <main className="flex-grow">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Introduction */}
          <div className="bg-gradient-to-r from-[#E67E22]/10 to-[#F39C12]/10 border-l-4 border-[#E67E22] rounded-lg p-6 mb-12">
            <div className="flex items-start">
              <AlertCircle className="text-[#E67E22] mr-4 flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="font-serif text-xl font-bold text-[#2C3E50] mb-2">
                  No Physical Shipping
                </h2>
                <p className="text-[#555] leading-relaxed">
                  WyldWoods Rural Retreat does not involve the shipping of physical products. 
                  Our services are limited to accommodation bookings and experiences.
                </p>
              </div>
            </div>
          </div>

          {/* Booking Confirmation Section */}
          <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 p-8 mb-8 animate-fade-in-up">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#E67E22] to-[#F39C12] flex items-center justify-center mr-4">
                <CheckCircle className="text-white" size={24} />
              </div>
              <h3 className="font-serif text-2xl font-bold text-[#2C3E50]">
                1. Booking Confirmation
              </h3>
            </div>
            <ul className="space-y-3 ml-16">
              <li className="flex items-start group">
                <span className="inline-block w-2 h-2 rounded-full bg-[#E67E22] mr-3 mt-2 flex-shrink-0 group-hover:scale-125 transition-transform"></span>
                <span className="text-[#555] leading-relaxed">
                  Upon successful payment, guests will receive a confirmation email or SMS with booking details.
                </span>
              </li>
              <li className="flex items-start group">
                <span className="inline-block w-2 h-2 rounded-full bg-[#E67E22] mr-3 mt-2 flex-shrink-0 group-hover:scale-125 transition-transform"></span>
                <span className="text-[#555] leading-relaxed">
                  If you do not receive a confirmation within 24 hours, please contact us at{" "}
                  <a href="mailto:support@wyldwoods.in" className="text-[#E67E22] hover:underline font-semibold">
                    support@wyldwoods.in
                  </a>
                  .
                </span>
              </li>
            </ul>
          </div>

          {/* Digital Services Section */}
          <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 p-8 mb-8 animate-fade-in-up" style={{ animationDelay: "100ms" }}>
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#E67E22] to-[#F39C12] flex items-center justify-center mr-4">
                <Mail className="text-white" size={24} />
              </div>
              <h3 className="font-serif text-2xl font-bold text-[#2C3E50]">
                2. Digital Services
              </h3>
            </div>
            <div className="ml-16">
              <p className="text-[#555] leading-relaxed">
                All services, including accommodation and activities, are confirmed digitally, and no physical shipping is involved.
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-[#2C3E50] to-[#34495E] text-white rounded-xl p-8 mb-8">
            <h3 className="font-serif text-2xl font-bold mb-6 text-center">
              For Any Inquiries
            </h3>
            <p className="text-center text-gray-300 mb-8">
              Regarding bookings or services, contact us at:
            </p>
            
            <div className="max-w-md mx-auto space-y-4">
              <div className="flex items-start group">
                <div className="w-10 h-10 rounded-lg bg-[#E67E22]/20 flex items-center justify-center mr-3 flex-shrink-0 group-hover:bg-[#E67E22] transition-colors duration-300">
                  <MapPin size={18} className="text-[#E67E22] group-hover:text-white transition-colors" />
                </div>
                <div className="text-gray-300 text-sm flex-1">
                  <p className="font-semibold text-white mb-1">WyldWoods Rural Retreat</p>
                  <p>61 Puthiamman Street, Panaiyur</p>
                  <p>East Coast Road</p>
                  <p>Edaikazhinadu – 603302</p>
                </div>
              </div>
              
              <div className="flex items-center group">
                <div className="w-10 h-10 rounded-lg bg-[#E67E22]/20 flex items-center justify-center mr-3 group-hover:bg-[#E67E22] transition-colors duration-300">
                  <Phone size={18} className="text-[#E67E22] group-hover:text-white transition-colors" />
                </div>
                <a href="tel:+919445771282" className="text-gray-300 text-sm hover:text-white transition-colors">
                  +91 94457 71282
                </a>
              </div>
              
              <div className="flex items-center group">
                <div className="w-10 h-10 rounded-lg bg-[#E67E22]/20 flex items-center justify-center mr-3 group-hover:bg-[#E67E22] transition-colors duration-300">
                  <Mail size={18} className="text-[#E67E22] group-hover:text-white transition-colors" />
                </div>
                <a 
                  href="mailto:support@wyldwoods.in" 
                  className="text-[#F39C12] hover:text-[#E67E22] transition-colors font-medium"
                >
                  support@wyldwoods.in
                </a>
              </div>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center text-[#E67E22] hover:text-[#F39C12] font-semibold transition-colors"
            >
              <HomeIcon size={20} className="mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
